import React, { useState, useEffect, useMemo } from 'react';
import { GameState, GameMode, Country, QuestionInstance } from '../types';
import { submitAnswer, advanceQuestion } from '../services/engine';
import { CountryInput } from './CountryInput';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { getGameData } from '../services/dataLoader';
import { checkAnswer } from '../services/templatesHelper';

interface Props {
  initialState: GameState;
  onExit: () => void;
  statMode: boolean;
  hintMode: boolean;
}

// Helper to format MM:SS.ms
const formatTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const deciseconds = Math.floor((ms % 1000) / 100);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${deciseconds}`;
};

const MULTI_QUESTION_COUNT = 5;
const MULTI_ANSWERS_PER_QUESTION = 5;

export const GameScreen: React.FC<Props> = ({ initialState, onExit, statMode, hintMode }) => {
  const [state, setState] = useState<GameState>(initialState);
  const [feedback, setFeedback] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [shake, setShake] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const isMultiMode = [GameMode.FIVE_FOR_FIVE, GameMode.ROLLING_FIVE, GameMode.GRID_GAUNTLET].includes(state.mode);
  const isRollingMode = state.mode === GameMode.ROLLING_FIVE;
  const data = getGameData();

  // Timer logic for Infinite Timed mode
  useEffect(() => {
    let interval: number;
    if (state.status === 'PLAYING' && state.mode === GameMode.INFINITE_TIMED) {
      interval = window.setInterval(() => {
        setElapsed(Date.now() - state.startTime);
      }, 100);
    }
    return () => clearInterval(interval);
  }, [state.status, state.mode, state.startTime]);

  const handleSelect = (country: any) => {
    const result = submitAnswer(state, country);
    
    // UI Feedback
    setFeedback({
      msg: result.message,
      type: result.valid ? 'success' : 'error'
    });

    if (!result.valid) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }

    // State update
    setState(result.newState);

    // Clear feedback after delay
    setTimeout(() => {
      setFeedback(null);
    }, 1500);
  };

  const handleNextQuestion = () => {
    setState((prev) => advanceQuestion(prev));
    setFeedback(null);
  };

  // Prepare sorted history
  const sortedHistory = useMemo(() => {
    return [...state.history].map(item => {
      const country = data.countries.find(c => c.iso3 === item.answerIso3);
      return { ...item, country };
    }).sort((a, b) => {
      const nameA = a.country?.name || a.answerIso3;
      const nameB = b.country?.name || b.answerIso3;
      return nameA.localeCompare(nameB);
    });
  }, [state.history, data]);

  const multiCollectedCount = useMemo(() => {
    if (!isMultiMode || !state.multiQuestions) return 0;
    return state.multiQuestions.reduce((total, entry) => total + entry.answers.length, 0);
  }, [isMultiMode, state.multiQuestions]);

  const usedCountryNames = useMemo(() => {
    if (!isMultiMode) return [];
    return Array.from(state.usedIsoCodes)
      .map((iso3) => data.indexes.byIso3[iso3]?.name ?? iso3)
      .sort((a, b) => a.localeCompare(b));
  }, [data.indexes.byIso3, isMultiMode, state.usedIsoCodes]);

  const getDifficultyLabel = (possible: number, available: number) => {
    if (possible === 0 || available === 0) return 'Impossible';
    const ratio = available / possible;
    if (ratio >= 0.75) return 'Easy';
    if (ratio >= 0.5) return 'Moderate';
    if (ratio >= 0.25) return 'Hard';
    return 'Brutal';
  };

  const buildAnswerStats = (question: QuestionInstance) => {
    const data = getGameData();
    const possibleAnswers = data.countries.filter(country =>
      checkAnswer(question.templateId, question.subjectIso3, country, data)
    );
    const excluded = new Set([...state.usedIsoCodes, ...state.burnedIsoCodes]);
    const remainingAnswers = possibleAnswers.filter(country => !excluded.has(country.iso3));
    const toNameList = (countries: Country[]) =>
      [...countries]
        .map((country) => country.name)
        .sort((a, b) => a.localeCompare(b));
    return {
      possible: possibleAnswers.length,
      remaining: remainingAnswers.length,
      difficulty: getDifficultyLabel(possibleAnswers.length, remainingAnswers.length),
      possibleList: hintMode ? toNameList(possibleAnswers) : [],
      remainingList: hintMode ? toNameList(remainingAnswers) : [],
    };
  };

  const answerStats = useMemo(() => {
    if (!statMode || isMultiMode) return null;
    return buildAnswerStats(state.currentQuestion);
  }, [state.currentQuestion, state.usedIsoCodes, state.burnedIsoCodes, statMode, hintMode, isMultiMode]);

  if (state.status === 'GAME_OVER') {
    const isMultiComplete = state.mode === GameMode.FIVE_FOR_FIVE
      && state.multiQuestions?.every(entry => entry.answers.length >= MULTI_ANSWERS_PER_QUESTION);
    const isWin = (
      [GameMode.INFINITE_TIMED, GameMode.QUESTION_SPRINT].includes(state.mode)
        && state.usedIsoCodes.size === getGameData().countries.length
    ) || Boolean(isMultiComplete);
    const finalTime = state.endTime && state.startTime ? state.endTime - state.startTime : 0;

    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-background p-4 text-center">
        <h2 className={`text-5xl font-black mb-4 ${isWin ? 'text-green-500' : 'text-destructive'}`}>
          {isWin ? 'COMPLETE!' : 'GAME OVER'}
        </h2>
        
        {state.mode === GameMode.INFINITE_TIMED ? (
          <p className="text-xl text-muted-foreground mb-8">
            Time: <span className="text-foreground font-bold">{formatTime(finalTime)}</span>
          </p>
        ) : state.mode === GameMode.QUESTION_SPRINT ? (
          <p className="text-xl text-muted-foreground mb-8">
            Questions Used: <span className="text-foreground font-bold">{state.score}</span>
          </p>
        ) : state.mode === GameMode.FIVE_FOR_FIVE ? (
          <p className="text-xl text-muted-foreground mb-8">
            Mistakes: <span className="text-foreground font-bold">{state.score}</span>
          </p>
        ) : state.mode === GameMode.ROLLING_FIVE ? (
          <p className="text-xl text-muted-foreground mb-8">
            Questions Completed: <span className="text-foreground font-bold">{state.score}</span>
          </p>
        ) : state.mode === GameMode.GRID_GAUNTLET ? (
          <p className="text-xl text-muted-foreground mb-8">
            Questions Completed: <span className="text-foreground font-bold">{state.score}</span>
          </p>
        ) : (
          <p className="text-xl text-muted-foreground mb-8">
            Final Score: <span className="text-foreground font-bold">{state.score}</span>
          </p>
        )}

        <div className="flex gap-4">
          <Button onClick={onExit} variant="outline" size="lg">Main Menu</Button>
        </div>
      </div>
    );
  }

  const renderHeaderRight = () => {
    if (state.mode === GameMode.INFINITE_TIMED) {
      const total = getGameData().countries.length;
      return (
        <div className="flex flex-col items-end">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Time</span>
          <span className="text-xl font-mono text-foreground">{formatTime(elapsed)}</span>
          <span className="text-[10px] text-muted-foreground">{state.usedIsoCodes.size}/{total} Found</span>
        </div>
      );
    }

    if (state.mode === GameMode.INFINITE_SCORE) {
      return (
        <div className="flex flex-col items-end">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Mode</span>
          <span className="text-xl">∞</span>
        </div>
      );
    }

    if (state.mode === GameMode.FIVE_FOR_FIVE) {
      const completed = state.multiQuestions?.filter((entry) => entry.answers.length >= MULTI_ANSWERS_PER_QUESTION).length ?? 0;
      return (
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-col items-end">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Questions</span>
            <span className="text-xl font-mono text-foreground">{completed}/{MULTI_QUESTION_COUNT}</span>
          </div>
          <div className="flex gap-1 text-xl">
            {Array.from({ length: state.maxLives }).map((_, i) => (
              <span key={i} className={i < state.lives ? 'text-red-500' : 'text-muted/20'}>
                ♥
              </span>
            ))}
          </div>
        </div>
      );
    }

    if (state.mode === GameMode.ROLLING_FIVE) {
      return (
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-col items-end">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Completed</span>
            <span className="text-xl font-mono text-foreground">{state.score}</span>
          </div>
          <span className="text-[10px] text-muted-foreground">Tokens: {state.recoveryTokens ?? 0}</span>
          <div className="flex gap-1 text-xl">
            {Array.from({ length: state.maxLives }).map((_, i) => (
              <span key={i} className={i < state.lives ? 'text-red-500' : 'text-muted/20'}>
                ♥
              </span>
            ))}
          </div>
        </div>
      );
    }

    if (state.mode === GameMode.GRID_GAUNTLET) {
      return (
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-col items-end">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Active</span>
            <span className="text-xl font-mono text-foreground">{state.multiQuestions?.length ?? 0}</span>
          </div>
          <div className="flex gap-1 text-xl">
            {Array.from({ length: state.maxLives }).map((_, i) => (
              <span key={i} className={i < state.lives ? 'text-red-500' : 'text-muted/20'}>
                ♥
              </span>
            ))}
          </div>
        </div>
      );
    }

    if (state.mode === GameMode.QUESTION_SPRINT) {
      const total = getGameData().countries.length;
      return (
        <div className="flex flex-col items-end">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Countries</span>
          <span className="text-xl font-mono text-foreground">{state.usedIsoCodes.size}/{total}</span>
          <span className="text-[10px] text-muted-foreground">Question {state.score}</span>
        </div>
      );
    }

    // Lives for Standard / Sudden Death
    return (
      <div className="flex flex-col items-end">
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Lives</span>
        <div className="flex gap-1 text-xl">
          {Array.from({ length: state.maxLives }).map((_, i) => (
            <span key={i} className={i < state.lives ? 'text-red-500' : 'text-muted/20'}>
              ♥
            </span>
          ))}
        </div>
      </div>
    );
  };

  const isGridGauntlet = state.mode === GameMode.GRID_GAUNTLET;
  const containerWidth = isMultiMode
    ? 'w-full md:max-w-[min(1400px,94vw)]'
    : 'w-full max-w-lg';
  const historyWidth = isMultiMode
    ? 'md:max-w-none md:w-[min(420px,32vw)] lg:w-[min(520px,30vw)]'
    : 'md:max-w-sm lg:max-w-md';

  return (
    <div className={`flex flex-col h-full ${containerWidth} mx-auto bg-background`}>
      {/* Header */}
      <header className="flex items-center justify-between p-4 border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex flex-col">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            {state.mode === GameMode.QUESTION_SPRINT
              ? 'Questions'
              : state.mode === GameMode.FIVE_FOR_FIVE
              ? 'Mistakes'
              : state.mode === GameMode.ROLLING_FIVE
              ? 'Completed'
              : state.mode === GameMode.GRID_GAUNTLET
              ? 'Completed'
              : 'Score'}
          </span>
          <span className="text-2xl font-black text-primary">{state.score}</span>
        </div>
        
        {renderHeaderRight()}
      </header>

      {/* Main Game Area */}
      <main className="flex-1 flex flex-col p-4 gap-6 overflow-hidden">
        {isMultiMode ? (
          <div className="flex flex-1 flex-col gap-6">
            <div className="flex-1 w-full min-h-0 flex flex-col">
              <h3 className="text-xs font-bold text-muted-foreground uppercase mb-2 pl-1">
                Collected ({multiCollectedCount})
              </h3>
              <div className="flex-1 overflow-y-auto no-scrollbar">
                {state.multiQuestions ? (
                  <div className="w-full overflow-hidden rounded-xl border border-border/60 bg-card/40">
                    <div
                      className="grid border-b border-border/60 bg-muted/40 text-[11px] font-semibold text-foreground"
                      style={{ gridTemplateColumns: 'minmax(180px, 1.2fr) repeat(5, minmax(0, 1fr))' }}
                    >
                      <div className="px-4 py-2">Question</div>
                      {Array.from({ length: MULTI_ANSWERS_PER_QUESTION }).map((_, index) => (
                        <div key={`multi-header-${index}`} className="px-2 py-2 text-center">
                          Answer {index + 1}
                        </div>
                      ))}
                    </div>
                    {state.multiQuestions.map((entry, rowIndex) => {
                      const isComplete = entry.answers.length >= MULTI_ANSWERS_PER_QUESTION;
                      return (
                        <div
                          key={entry.question.id}
                          className={`grid items-center border-b border-border/40 last:border-b-0 ${
                            isComplete ? 'animate-in fade-in zoom-in duration-300' : ''
                          }`}
                          style={{ gridTemplateColumns: 'minmax(180px, 1.2fr) repeat(5, minmax(0, 1fr))' }}
                        >
                          <div className="flex items-start gap-3 px-4 py-3 text-sm font-semibold text-foreground/90">
                            <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                              {rowIndex + 1}
                            </span>
                            <span className="leading-snug">{entry.question.text}</span>
                          </div>
                          {Array.from({ length: MULTI_ANSWERS_PER_QUESTION }).map((_, colIndex) => {
                            const iso3 = entry.answers[colIndex];
                            const country = iso3 ? data.indexes.byIso3[iso3] : null;
                            return (
                              <div key={`${entry.question.id}-${colIndex}`} className="px-2 py-2">
                                <div
                                  className={`flex items-center justify-center gap-2 rounded-lg border px-2 py-2 text-[11px] font-medium ${
                                    country
                                      ? 'bg-background border-border text-foreground shadow-sm'
                                      : 'border-dashed border-border/60 text-muted-foreground/70'
                                  }`}
                                >
                                  {country ? (
                                    <>
                                      <img
                                        src={country.flagUrl}
                                        alt=""
                                        className="h-4 w-6 rounded-[3px] object-cover shadow-sm"
                                        loading="lazy"
                                      />
                                      <span className="truncate max-w-[110px]">{country.name}</span>
                                    </>
                                  ) : (
                                    <span>-</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="rounded-xl border border-primary/20 bg-card/70 p-4 shadow-sm">
              <div className="h-6 flex items-center justify-center w-full">
                {feedback ? (
                  <span className={`px-3 py-1 rounded-full text-sm font-bold animate-in fade-in zoom-in duration-200 ${
                    feedback.type === 'success' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                  }`}>
                    {feedback.msg}
                  </span>
                ) : (
                  <span className="h-6 block"></span>
                )}
              </div>
              {isGridGauntlet ? (
                <div className="mt-3 flex flex-col items-center">
                  <div className="w-full max-w-sm relative z-20">
                    <CountryInput 
                      onSelect={handleSelect} 
                      disabled={false}
                      usedIsoCodes={state.usedIsoCodes}
                      burnedIsoCodes={state.burnedIsoCodes}
                      allowUsed={isRollingMode && (state.recoveryTokens ?? 0) > 0}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-start">
                    <div className="w-full md:max-w-sm relative z-20">
                      <CountryInput 
                        onSelect={handleSelect} 
                        disabled={false}
                        usedIsoCodes={state.usedIsoCodes}
                        burnedIsoCodes={state.burnedIsoCodes}
                        allowUsed={isRollingMode && (state.recoveryTokens ?? 0) > 0}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                        Used Countries
                      </div>
                      {usedCountryNames.length === 0 ? (
                        <span className="text-xs text-muted-foreground">-</span>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {usedCountryNames.map((name) => (
                            <span
                              key={name}
                              className="inline-flex items-center rounded-full border border-border/60 bg-background px-2 py-1 text-[11px] font-medium text-foreground"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {isRollingMode && (
                    <div className="mt-3 text-xs text-muted-foreground">
                      Recovery Tokens: <span className="font-semibold text-foreground">{state.recoveryTokens ?? 0}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 md:flex-row md:items-stretch">
            {/* Active Interaction Card */}
            <div className={`w-full transition-transform md:flex-1 ${shake ? 'animate-[shake_0.5s_ease-in-out]' : ''}`}>
              <Card className="border-primary/20 bg-gradient-to-br from-card to-accent/20 overflow-visible">
                <CardContent className="pt-6 pb-6 flex flex-col items-center gap-6">
                  {/* Question Text */}
                  <div className="text-center space-y-4 w-full">
                    <>
                      {state.currentQuestion.evidence?.kind === 'flag' && state.currentQuestion.evidence.url && (
                        <div className="flex justify-center">
                          <img
                            src={state.currentQuestion.evidence.url}
                            alt={state.currentQuestion.evidence.label ?? 'Flag'}
                            className="h-20 w-auto rounded-md border border-border/60 shadow-sm"
                          />
                        </div>
                      )}
                      <div className="text-xs text-primary/70 uppercase tracking-widest font-semibold">
                        {state.currentQuestion.category}
                      </div>
                      <h2 className="text-2xl font-bold leading-tight">
                        {state.currentQuestion.text}
                      </h2>
                      {answerStats && (
                        <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
                          <div className="relative group">
                            <span className="rounded-full bg-muted/60 px-2 py-1">
                              Possible: {answerStats.possible}
                            </span>
                            {hintMode && (
                              <div className="absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lg border border-border bg-card p-3 text-xs text-foreground opacity-0 shadow-xl transition duration-200 group-hover:opacity-100">
                                <div className="font-semibold text-muted-foreground mb-2">Possible Answers</div>
                                <div className="max-h-40 overflow-y-auto pr-1">
                                  <ul className="space-y-1">
                                    {answerStats.possibleList.map((name) => (
                                      <li key={name}>{name}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="relative group">
                            <span className="rounded-full bg-muted/60 px-2 py-1">
                              Available: {answerStats.remaining}
                            </span>
                            {hintMode && (
                              <div className="absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lg border border-border bg-card p-3 text-xs text-foreground opacity-0 shadow-xl transition duration-200 group-hover:opacity-100">
                                <div className="font-semibold text-muted-foreground mb-2">Available Answers</div>
                                <div className="max-h-40 overflow-y-auto pr-1">
                                  <ul className="space-y-1">
                                    {answerStats.remainingList.map((name) => (
                                      <li key={name}>{name}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                          <span className="rounded-full bg-muted/60 px-2 py-1">
                            Difficulty: {answerStats.difficulty}
                          </span>
                        </div>
                      )}
                    </>
                  </div>

                  {/* Feedback Message */}
                  <div className="h-6 flex items-center justify-center w-full">
                    {feedback ? (
                      <span className={`px-3 py-1 rounded-full text-sm font-bold animate-in fade-in zoom-in duration-200 ${
                        feedback.type === 'success' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                      }`}>
                        {feedback.msg}
                      </span>
                    ) : (
                      <span className="h-6 block"></span>
                    )}
                  </div>

                  {/* Input */}
                  <div className="w-full max-w-sm relative z-20">
                    <CountryInput 
                      onSelect={handleSelect} 
                      disabled={false}
                      usedIsoCodes={state.usedIsoCodes}
                      burnedIsoCodes={state.burnedIsoCodes}
                      allowUsed={isRollingMode && (state.recoveryTokens ?? 0) > 0}
                    />
                  </div>

                  {state.mode === GameMode.QUESTION_SPRINT && (
                    <div className="w-full flex justify-center">
                      <Button variant="secondary" size="sm" onClick={handleNextQuestion}>
                        Next Question
                      </Button>
                    </div>
                  )}
                  
                </CardContent>
              </Card>
            </div>

            {/* History / Collection */}
            <div className={`flex-1 w-full min-h-0 flex flex-col ${historyWidth}`}>
              <h3 className="text-xs font-bold text-muted-foreground uppercase mb-2 pl-1">
                Collected ({sortedHistory.filter(h => h.isCorrect).length})
              </h3>
              <div className="flex-1 overflow-y-auto overflow-x-auto no-scrollbar">
                {sortedHistory.length === 0 ? (
                  <div className="h-full flex items-center justify-center rounded-xl border border-border/50 bg-accent/10 p-3 text-sm text-muted-foreground italic opacity-50">
                    Correct answers will appear here...
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 content-start rounded-xl border border-border/50 bg-accent/10 p-3">
                    {sortedHistory.map((item, idx) => (
                      <div 
                        key={`${item.answerIso3}-${idx}`} 
                        className={`
                          inline-flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs font-medium transition-colors
                          ${item.isCorrect 
                            ? 'bg-card border-border text-foreground hover:border-primary/50' 
                            : 'bg-destructive/10 border-destructive/30 text-destructive'
                          }
                        `}
                      >
                        {item.country && (
                          <img 
                            src={item.country.flagUrl} 
                            alt="" 
                            className="w-5 h-3.5 object-cover rounded-[2px] shadow-sm grayscale-[0.1]" 
                            loading="lazy"
                          />
                        )}
                        <span className="truncate max-w-[100px]">
                          {item.country?.name || item.answerIso3}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="w-full">
          <Button variant="ghost" size="sm" onClick={onExit} className="w-full text-muted-foreground hover:text-destructive transition-colors">
            End Game
          </Button>
        </div>
      </main>
    </div>
  );
};
